import { randomUUID } from 'node:crypto';

export type MediaOwnerType =
  | 'product'
  | 'category'
  /** Logos and wordmarks — the store's identity, not a page's content. */
  | 'logo'
  /** Homepage banners and hero imagery. */
  | 'homepage'
  | 'storefront'
  | 'theme'
  | 'review'
  | 'document'
  | 'other';

export const MEDIA_OWNER_TYPES: MediaOwnerType[] = [
  'product',
  'category',
  'logo',
  'homepage',
  'storefront',
  'theme',
  'review',
  'document',
  'other',
];

/** Documents are private: signed URLs only, never a public bucket URL. */
export function isPrivateOwnerType(ownerType: string): boolean {
  return ownerType === 'document';
}

const EXTENSION_BY_MIME: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/avif': 'avif',
  'application/pdf': 'pdf',
  'text/plain': 'txt',
  'text/csv': 'csv',
  'application/zip': 'zip',
};

export function extensionFor(mimeType: string, fileName?: string): string {
  const known = EXTENSION_BY_MIME[mimeType.toLowerCase()];
  if (known) return known;
  const fromName = fileName?.match(/\.([a-z0-9]{1,8})$/i)?.[1]?.toLowerCase();
  return fromName ?? 'bin';
}

/** The folder every key lives under, one per environment sharing the bucket. */
export const MERCHANTS_SEGMENT = 'merchants';

/**
 * The top-level folder every key is written under, separating environments
 * that share one bucket.
 *
 * Local development and production point at the *same* R2 bucket, so without
 * this a developer's test upload and a real merchant's product photo land in
 * the same folder — and a cleanup script written for one would reach the
 * other. `test/` and `production/` keep them apart at the first path segment,
 * which is also the level R2 lifecycle rules and bucket-scoped API tokens
 * operate on.
 *
 * Defaults to `test`. An environment that forgets to set this writes into the
 * throwaway half of the bucket, never into production data.
 */
export function environmentPrefix(): string {
  const raw = (process.env.MEDIA_ENV_PREFIX ?? 'test').trim().toLowerCase();

  // A prefix is part of a path, so anything that could climb out of it or
  // break key parsing is refused outright rather than quietly sanitised into
  // something that merely looks safe.
  if (!/^[a-z0-9][a-z0-9_-]*$/.test(raw)) {
    throw new Error(
      `MEDIA_ENV_PREFIX "${raw}" is not a valid path segment — use lowercase letters, digits, "-" or "_".`
    );
  }
  return raw;
}

/**
 * Stands in for the variant id on an image belonging to the product as a whole
 * rather than to one variant.
 *
 * The segment is always present so every product key has the same depth: a
 * fixed shape is what lets a prefix listing, a lifecycle rule or a
 * delete-by-prefix target exactly one product without having to guess how many
 * segments deep it sits. It cannot collide with a real variant id, which is
 * always a UUID.
 */
export const SHARED_VARIANT_SEGMENT = '_shared';

/** Generated renditions live here, beside the original they came from. */
export const THUMBS_SEGMENT = 'thumbs';

export interface BuildKeyInput {
  organizationId: string;
  ownerType: MediaOwnerType;
  ownerId?: string | null;
  storeId?: string | null;
  /**
   * The variant this image belongs to, when it belongs to just one.
   *
   * Product images are uploaded against the product today, so this is usually
   * unset and the key carries {@link SHARED_VARIANT_SEGMENT} instead. The
   * layout reserves the level regardless, so a per-variant upload can be added
   * later without moving a single existing object.
   */
  variantId?: string | null;
  mimeType: string;
  fileName?: string;
  /** Set for derivatives: `400w` and friends. These live under `thumbs/`. */
  variantSuffix?: string;
  /** Supplied only by callers that need a deterministic id (e.g. migration). */
  assetId?: string;
}

/**
 * Builds the object key for a new asset.
 *
 * The layout:
 *
 * ```
 *   {env}/merchants/{orgId}/{storeId}/products/{productId}/{variantId}/{assetId}.jpg
 *   {env}/merchants/{orgId}/{storeId}/products/{productId}/{variantId}/thumbs/{assetId}_400w.jpg
 *   {env}/merchants/{orgId}/{storeId}/account/logos/{assetId}.png
 *   {env}/merchants/{orgId}/{storeId}/account/homepage/{assetId}.png
 * ```
 *
 * It reads top-down the way the business does — environment, merchant, store,
 * kind, then the thing itself — so every level anyone would want to operate on
 * is a prefix. Deleting one merchant, listing a single store's media, or
 * expiring test data are then all prefix operations, no index required.
 *
 * **`orgId` and `storeId` are not optional.** They are what makes
 * `assertKeyBelongsTo` able to refuse another tenant's key at all, and what
 * makes "delete everything belonging to this merchant" a single prefix. A
 * layout of `merchants/products/{productId}/…` would put every merchant's
 * media in one folder with nothing to separate them by.
 *
 * Derivatives sit in a `thumbs/` folder rather than as `_400w` siblings, so a
 * CDN purge or a re-cut can target every generated rendition without touching
 * a single file the merchant actually uploaded. The size stays in the
 * filename because several renditions share the folder.
 *
 * The caller-supplied filename deliberately does **not** appear in the key.
 * Sanitised user filenames in keys cause encoding and case-sensitivity bugs,
 * invite path traversal, and leak customer-supplied strings into public URLs;
 * the original name is kept as `MediaAsset.fileName` metadata instead. Keys
 * are also immutable — replacing an image writes a new key — which is what
 * makes a one-year `Cache-Control` on the CDN correct without cache busting.
 */
export function buildMediaKey(input: BuildKeyInput): { key: string; assetId: string } {
  const assetId = input.assetId ?? randomUUID();
  const ext = extensionFor(input.mimeType, input.fileName);
  const suffix = input.variantSuffix ? `_${input.variantSuffix}` : '';
  const file = `${assetId}${suffix}.${ext}`;

  const scope = ownerScope(input);
  const folder = input.variantSuffix ? `${scope}/${THUMBS_SEGMENT}` : scope;

  return {
    key: `${environmentPrefix()}/${MERCHANTS_SEGMENT}/${input.organizationId}/${folder}/${file}`,
    assetId,
  };
}

/** Branding and page media, kept together under one folder per store. */
export const ACCOUNT_SEGMENT = 'account';
/** Catalogue media. A literal segment so a product id is never the first thing. */
export const PRODUCTS_SEGMENT = 'products';

function ownerScope(input: BuildKeyInput): string {
  // Media that predates a store, or belongs to the organization rather than to
  // any one of its stores. Underscored so it cannot be mistaken for a store id.
  const store = input.storeId ?? '_org';

  switch (input.ownerType) {
    /*
     * The shape a merchant's own mental model uses: a photo belongs to a
     * variant, of a product, in a store. The literal `products/` segment keeps
     * a product id from sitting directly under the store, where it could not
     * be told apart from `account/` at a glance or in a lifecycle rule.
     */
    case 'product':
      return `${store}/${PRODUCTS_SEGMENT}/${input.ownerId ?? 'unassigned'}/${input.variantId ?? SHARED_VARIANT_SEGMENT}`;

    /*
     * The store's own identity, rather than any one product or page. Grouped
     * under `account/` so a merchant's branding can be found, replaced or
     * exported without walking the catalogue.
     */
    case 'logo':
      return `${store}/${ACCOUNT_SEGMENT}/logos`;
    case 'homepage':
      return `${store}/${ACCOUNT_SEGMENT}/homepage`;

    /*
     * Everything else keeps a literal owner-type segment where a product key
     * has its product id. No id is ever the string "categories", so the two
     * can never be confused when reading a key or matching a prefix — and a
     * category image gains nothing from a variant level it can never have.
     */
    case 'category':
      return `${store}/categories/${input.ownerId ?? 'unassigned'}`;
    case 'review':
      return `${store}/reviews/${input.ownerId ?? 'unassigned'}`;
    case 'storefront':
      return `${store}/${ACCOUNT_SEGMENT}/storefront`;
    case 'theme':
      return `${store}/${ACCOUNT_SEGMENT}/themes`;
    case 'document':
      return `${store}/documents`;
    default:
      return `${store}/other`;
  }
}

/**
 * Every key contains the organization UUID, and that segment is derived
 * server-side from the session — never from a request body. This check is the
 * belt to that braces: it refuses any key that would reach outside the calling
 * org's prefix, so a bug in a future caller cannot turn into cross-tenant
 * access.
 *
 * Two layouts are accepted, because keys are immutable and objects written
 * before the environment-prefixed layout are still read, migrated and deleted
 * through this same path:
 *
 * ```
 *   {env}/merchants/{orgId}/...   current
 *   {orgId}/...                   legacy
 * ```
 *
 * Both are matched exactly, not by `includes()` — an org id appearing
 * *somewhere* in a key says nothing about which tenant owns the object.
 */
export function assertKeyBelongsTo(organizationId: string, key: string): void {
  const normalized = key.replace(/^\/+/, '');

  // Checked before the prefix match: a traversal segment can make a key that
  // starts inside the org prefix resolve somewhere else entirely.
  if (normalized.includes('..')) {
    throw new Error(`Storage key "${key}" contains a traversal segment`);
  }

  const segments = normalized.split('/');
  const current =
    segments[1] === MERCHANTS_SEGMENT && segments[2] === organizationId;
  const legacy = segments[0] === organizationId;

  if (!current && !legacy) {
    throw new Error(`Storage key "${key}" does not belong to organization ${organizationId}`);
  }
}

/** Kept for display only — never used to build a key. */
export function sanitizeFileName(name: string): string {
  const cleaned = name.replace(/[^\w.-]+/g, '_').slice(0, 120);
  // A name made entirely of separators sanitizes to "_" or "..", which is a
  // useless label in the media list and, worse, looks like a path fragment.
  // Only a name with something readable left in it survives.
  return /[a-z0-9]/i.test(cleaned) ? cleaned : 'file';
}

/** Issued documents. Kept beside the merchant's media, under its own segment. */
export const INVOICES_SEGMENT = 'invoices';

export interface BuildInvoiceKeyInput {
  organizationId: string;
  storeId: string;
  /** Financial year label, e.g. "2026-27". */
  financialYear: string;
  /** The canonical number, e.g. "INV/2026-27/00001234". */
  invoiceNumber: string;
  issuedAt: Date;
}

/**
 * The object key for a rendered invoice PDF.
 *
 * ```
 *   {env}/merchants/{orgId}/{storeId}/invoices/{fy}/INV_2026-27_00001234_{epoch}.pdf
 * ```
 *
 * Same top-down shape as `buildMediaKey`, for the same reason: every level
 * anyone would operate on is a prefix, so "everything this store issued in
 * 2026-27" is a listing rather than a scan of every invoice it has ever raised.
 * That prefix is what a retention rule and a GSTR export both want.
 *
 * The invoice number cannot go in raw — `INV/2026-27/00001234` contains
 * slashes, which object storage would read as folders — so it is flattened to
 * underscores. The database keeps the canonical form.
 *
 * The timestamp suffix makes every write a NEW key. That is deliberate: an
 * issued document must never be silently overwritten by a re-render, so a
 * second render lands beside the first and the mismatch is visible rather than
 * lost. It also means the key cannot be derived from the invoice number, which
 * is why `Invoice.pdfStorageKey` is stored rather than computed.
 */
export function buildInvoiceKey(input: BuildInvoiceKeyInput): string {
  const file = `${input.invoiceNumber.replace(/[^A-Za-z0-9._-]+/g, '_')}_${input.issuedAt.getTime()}.pdf`;

  return [
    environmentPrefix(),
    MERCHANTS_SEGMENT,
    input.organizationId,
    input.storeId,
    INVOICES_SEGMENT,
    input.financialYear,
    file,
  ].join('/');
}
